import { useCallback, useEffect, useRef, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import { Avatar, useToast } from "../../ui";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import s from "../PatientPortal/clientApp.module.css";
import md from "../common/manageDesktop.module.css";

export default function AdminOwnerSocialSettings() {
  const toast = useToast();
  const isApp = useAdminHubUi();
  const isMobile = useIsMobile(768);
  const desktop = !isApp && !isMobile;
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [theme, setTheme] = useState("");
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getStaffMemberProfile();
      const p = data.profile || {};
      setProfile(p);
      setBio(p.profile_bio || "");
      setTheme(p.profile_theme || "");
    } catch {
      toast.error("Не удалось загрузить профиль");
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const r = await clinicAPI.uploadStaffMemberCover(file);
      setProfile((prev) => (prev ? { ...prev, profile_cover_url: r.url } : prev));
      toast.success("Обложка обновлена");
    } catch {
      toast.error("Не удалось загрузить обложку");
    } finally {
      setCoverUploading(false);
    }
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await clinicAPI.updateStaffMemberProfile({
        profile_bio: bio,
        profile_theme: theme || null,
      });
      toast.success("Сохранено");
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const coverUrl = mediaUrl(profile?.profile_cover_url);
  const activeTheme = TRAINER_COLOR_PRESETS.find((p) => p.id === theme);
  const openCoverPicker = () => {
    if (!coverUploading) coverInputRef.current?.click();
  };

  return (
    <div className={s.socialEditPage}>
      <section className={s.socialEditCoverWrap} aria-label="Обложка профиля">
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={uploadCover}
        />
        <div
          className={`${s.socialEditCover} ${desktop ? md.desktopSocialEditCover : ""}`}
          style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
          role="presentation"
        >
          <div className={s.socialEditCoverFade} aria-hidden />
          <button
            type="button"
            className={`${s.socialEditCoverBtn} ${desktop ? md.desktopSocialCoverUploadBtn : ""}`}
            onClick={openCoverPicker}
            disabled={coverUploading}
          >
            <span>
              {coverUploading
                ? "Загрузка…"
                : coverUrl
                  ? "Заменить обложку"
                  : "Загрузить обложку"}
            </span>
          </button>
        </div>

        <div className={s.socialEditIdentity}>
          <Avatar
            name={profile?.full_name}
            src={mediaUrl(profile?.photo_url)}
            size="lg"
            className={s.socialEditAvatar}
          />
          <div className={s.socialEditIdentityText}>
            <p className={s.socialEditName}>{profile?.full_name || "Профиль"}</p>
          </div>
        </div>
      </section>

      <div className={s.socialEditCard}>
        <section className={s.socialEditBlock}>
          <h3 className={s.socialEditBlockTitle}>О себе</h3>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="Расскажите о себе, целях, увлечениях…"
            className={s.socialEditBio}
          />
          <p className={s.socialEditCounter}>{bio.length} / 2000</p>
        </section>

        <section className={s.socialEditBlock}>
          <h3 className={s.socialEditBlockTitle}>Цвет профиля</h3>
          <p className={s.socialEditBlockHint}>
            Акцент на вашей странице
          </p>
          <div className={s.socialThemePicker}>
            <button
              type="button"
              className={!theme ? s.socialThemeChipOn : s.socialThemeChip}
              onClick={() => setTheme("")}
            >
              <span className={s.socialThemeChipDotDefault} />
              <span>По умолчанию</span>
            </button>
            {TRAINER_COLOR_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={theme === p.id ? s.socialThemeChipOn : s.socialThemeChip}
                onClick={() => setTheme(p.id)}
              >
                <span
                  className={s.socialThemeChipDot}
                  style={{ background: p.color }}
                />
                <span>{p.label}</span>
              </button>
            ))}
          </div>
          {activeTheme ? (
            <p className={s.socialThemePreview} style={{ color: activeTheme.color }}>
              Выбран: {activeTheme.label}
            </p>
          ) : null}
        </section>
      </div>

      <div className={s.socialEditFooter}>
        <button
          type="button"
          className={s.socialEditSaveBtn}
          onClick={save}
          disabled={saving}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}
